import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { isEmailValid } from "../utils/utilities";
import AlertBox from "../components/common/AlertBox";
import Input from "../components/sub-component/Input";
import Button from "../components/sub-component/Button";
import ProcessingIndicator from "../components/common/ProcessingIndicator";
import { candidateInvite, candidatePublicInvite } from "../redux/thunks/Auth";
import { ReactComponent as ErrorIcon } from "../assets/icons/basic/x-circle-error.svg";
import { ReactComponent as TestFuseWatermarkIcon } from "../assets/icons/candidate-assessment/Watermark Light.svg";

function CandidateInvite() {
  const dispatch = useDispatch();
  const [email, setEmail] = useState();
  const [alert, setAlert] = useState(null);
  const [emailError, setEmailError] = useState("");
  const [isloading, setIsLoading] = useState(true);
  const [emailSent, setEmailSent] = useState(false);
  const [inviteData, setInviteData] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleChange = (value) => {
    setEmail(`${value}`.trim());
    if (alert) {
      setAlert();
    }
  };

  useEffect(() => {
    setProcessing(true);
    const fetchData = async () => {
      const searchParams = new URLSearchParams(window.location.search);
      const invite_id = searchParams.get("invite_id");
      const valid = await dispatch(candidateInvite({ invite_id: invite_id }));
      setInviteData(valid?.payload);
      if (valid.payload?.is_valid === false) {
        window.location.replace("/assessment-unavailable");
      }
      setProcessing(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsLoading(false);
    }, 4000);

    return () => clearTimeout(timeout);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email?.trim() || !isEmailValid(email?.trim())) {
      setEmailError("Please enter an email");
      return;
    } else {
      setEmailError(""); // Clear it when valid
    }

    try {
      setProcessing(true);
      const searchParams = new URLSearchParams(window.location.search);
      const inviteId = searchParams.get("invite_id");
      const response = await dispatch(candidateInvite({ invite_id: inviteId }));
      const { tenant_id, spec_id, tenant_status } = response.payload;
      const candiadteInvite = await dispatch(
        candidatePublicInvite({
          email: email.trim(),
          tenant_id,
          spec_id,
          tenant_status,
        }),
      );
      setProcessing(false);
      // setEmailSent(true);
      if (
        candiadteInvite?.payload ===
        "Provided email is already a non-candidate user on the platform"
      ) {
        setEmailSent(false);
        setEmailError(candiadteInvite?.payload);
      } else {
        setEmailSent(true);
      }
      setAlert(
        <AlertBox
          description="Invite sent"
          color="success"
          title="Success"
          onCloseAlert={() => setAlert(null)}
        />,
      );

      if (candiadteInvite?.error?.message === "Rejected") {
        setAlert(
          <AlertBox
            description="Provided email is already a non-candidate user on the platform"
            color="danger"
            title="Error"
            onCloseAlert={() => setAlert(null)}
          />,
        );
      }
    } catch (error) {
      setProcessing(false);

      if (
        error.response &&
        (error.response.status === 400 || error.response.status === 409)
      ) {
        setAlert(
          <AlertBox
            description={`Error occurred: ${error.response.data.message}`}
            color="danger"
            title="Error"
            onCloseAlert={() => setAlert(null)}
          />,
        );
      } else {
        setAlert(
          <AlertBox
            description={`Error occurred: ${error.message}`}
            color="danger"
            title="Error"
            onCloseAlert={() => setAlert(null)}
          />,
        );
      }

      window.location.replace("/assessment-unavailable");
    }
  };

  // Converts seconds → "X hour(s) Y min(s)"
  const formatDuration = (durationInSeconds) => {
    const totalMinutes = Math.floor(Number(durationInSeconds) / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    let result = "";
    if (hours > 0) result += `${hours} hour${hours > 1 ? "s" : ""}`;
    if (minutes > 0) {
      if (result) result += " ";
      result += `${minutes} min${minutes > 1 ? "s" : ""}`;
    }

    return result || "0 mins";
  };

  // Converts seconds from now → "today", "tomorrow", "in X days"
  const getDeadlineTextFromNow = (secondsFromNow) => {
    if (!secondsFromNow || Number(secondsFromNow) <= 0) {
      return "there is no deadline for this assessment";
    }

    const days = Math.floor(Number(secondsFromNow) / (60 * 60 * 24));

    if (days === 0) return " today";
    if (days === 1) return " 1 day";
    return ` ${days} days`;
  };

  return (
    <>
      {alert}
      {isloading ? (
        <div
          style={{
            width: "100%",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <ProcessingIndicator />
        </div>
      ) : (
        <div className="candidate-invite-main-container">
          <div className="candidate-invite-container">
            <div className="assessment-header">
              {inviteData?.tenant_logo ? (
                <img
                  className="assessment-header-icon"
                  src={inviteData?.tenant_logo}
                  alt=""
                />
              ) : (
                <div className="assessment-header-icon-initial">
                  <span className="body-1 white">
                    {(inviteData?.tenant_name?.slice(0, 2) || "").toUpperCase()}
                  </span>
                </div>
              )}
              <span className="headline-1 dark-100">
                {inviteData?.tenant_name}
              </span>
            </div>
            {emailSent ? (
              <div
                className="d-flex flex-column align-items-start"
                style={{ gap: "24px" }}
              >
                <span className="headline-3 text-start">Check your email.</span>
                <span className="body-2 dark-50 text-start">
                  An invite email has been sent to{" "}
                  <span className="dark-100 semibold">{email}</span> Please
                  click on the link in the email to start your assessment.
                </span>
                <span className="body-2 dark-50 text-start">
                  If you don’t receive an invite email in your inbox, please
                  check mailbox folders and spam.
                </span>
              </div>
            ) : (
              <div
                className="d-flex flex-column align-items-start"
                style={{ gap: "24px" }}
              >
                <span className="headline-3 text-start">
                  Welcome to assessment for the <span>{inviteData?.title}</span>{" "}
                  position.
                </span>
                <span className="body-2 dark-50 text-start">
                  This online assessment will help us to understand your skills
                  and is a mandatory step in the evaluation process.
                </span>
                <form onSubmit={handleSubmit}>
                  {emailError && (
                    <div className="error-text mb-4">
                      <div style={{ width: "30px" }}>
                        <ErrorIcon />
                      </div>
                      {emailError}
                    </div>
                  )}
                  <div className="d-flex flex-column">
                    <label
                      className="body-2 dark-50 mb-2 text-start"
                      style={{ marginLeft: "24px" }}
                    >
                      Please enter your email to continue:
                    </label>
                    <Input
                      // placeholder="Email"
                      onChange={(e) => {
                        handleChange(e.target.value);
                      }}
                      type={"text"}
                      value={email}
                      onBoardingInput
                      varrient={"primary"}
                      isRequired={true}
                      className={emailError ? "error-input" : ""}
                    />
                  </div>
                  <div className="mt-4">
                    <Button
                      btn="ragular-btn"
                      varrient="primary"
                      textClass="body-1"
                      processing={processing}
                      type={"submit"}
                      title={"Get Invite"}
                      style={{ width: "100%", height: "40px" }}
                    />
                  </div>
                  <div
                    className="d-flex align-items-start"
                    style={{ marginTop: "24px" }}
                  >
                    <span className="body-2 dark-50 text-start">
                      By proceeding to create an account on our platform, you
                      agree to Testfuse{" "}
                      <a
                        href="https://testfuse.com/terms-of-service/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="custom-link"
                      >
                        Terms and Conditions
                      </a>{" "}
                      and{" "}
                      <a
                        href="https://testfuse.com/privacy-policy/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="custom-link"
                      >
                        Privacy Policy
                      </a>
                      .
                    </span>
                  </div>
                </form>
              </div>
            )}
          </div>
          <div className="watermark mt-3">
            <TestFuseWatermarkIcon style={{ height: "50px" }} />
          </div>
        </div>
      )}
    </>
  );
}

export default CandidateInvite;
